<template>

    <div class="featured-game">
        <div class="featured-cover">
            <video v-if="trailerLink" :src="trailerLink" autoplay muted loop playsinline preload="auto" />
            <img v-if="!trailerLink && bupImage" :src="bupImage" alt="" />
            <div class="featured-gradient"></div>
        </div>
        <div class="featured-content">

            <div class="featured-poster">
                <img :src="feature.coverArt" :alt="feature.title" />
            </div>

            <div class="featured-info">
                <h2 class="featured-title">{{ feature.title }}</h2>

                <div class="featured-meta">
                    <span v-if="formattedDate" class="meta-date">{{ formattedDate }}</span>
                    <span v-if="genres.length" class="meta-genres">
                        <span v-for="genre in genres.slice(0, 3)" :key="genre.id" class="genre-pill">{{ genre.name }}</span>
                    </span>
                    <span v-if="primaryDeveloper" class="meta-developer">{{ primaryDeveloper }}</span>
                </div>

                <div class="featured-badges">
                    <div v-if="metacritic" class="badge-metascore" :class="metascoreClass">{{ metacritic }}</div>
                    <div v-if="rating" class="badge-rating">
                        <span class="star-rating">
                            <span class="stars-back">★★★★★</span>
                            <span class="stars-front" :style="{ width: ratingPercent + '%' }">★★★★★</span>
                        </span>
                        <span class="rating-value">{{ rating.toFixed(1) }}</span>
                        <span v-if="ratingsCount" class="rating-count">({{ ratingsCount.toLocaleString() }})</span>
                    </div>
                    <div v-if="esrbLabel" class="badge-esrb">{{ esrbLabel }}</div>
                </div>

                <p v-if="descriptionRaw" class="featured-description">{{ descriptionRaw }}</p>

                <button v-ripple class="play-button" @click="$router.push(`/theater?mode=game&id=${feature.id}`).catch(() => {})">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                        <rect x="2" y="5" width="20" height="14" rx="5" stroke="#fff" stroke-width="2"/>
                        <path d="M9 10L9 14" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
                        <path d="M11 12H7" stroke="#fff" stroke-width="2" stroke-linecap="round"/>
                        <circle cx="17" cy="10" r="1" fill="#fff"/>
                        <circle cx="15" cy="14" r="1" fill="#fff"/>
                    </svg>
                    Play Now
                </button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DesktopHero',

    props: {
        feature: Object
    },

    data() {
        return {
            trailerLink: null,
            bupImage: null,
            released: null,
            genres: [],
            metacritic: null,
            rating: null,
            ratingsCount: null,
            esrbRating: null,
            developers: [],
            descriptionRaw: null
        }
    },

    computed: {
        formattedDate() {
            if (!this.released) return null
            const [year, month, day] = this.released.split('-').map(Number)
            const date = new Date(year, month - 1, day)
            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        },

        primaryDeveloper() {
            return this.developers.length ? this.developers[0].name : null
        },

        metascoreClass() {
            if (!this.metacritic) return ''
            if (this.metacritic >= 75) return 'high'
            if (this.metacritic >= 50) return 'mid'
            return 'low'
        },

        ratingPercent() {
            return this.rating ? (this.rating / 5) * 100 : 0
        },

        esrbLabel() {
            if (!this.esrbRating) return null
            const abbreviations = {
                'everyone': 'E',
                'everyone-10-plus': 'E10+',
                'teen': 'T',
                'mature': 'M',
                'adults-only': 'AO',
                'rating-pending': 'RP'
            }
            return abbreviations[this.esrbRating.slug] || this.esrbRating.name
        }
    },

    mounted() {
        this.getGameDetails()
        this.getTrailer()
    },

    methods: {
        async getGameDetails(){
            //move rawg to node requ games too
            const detailrequest = await window.afetch(`/game/meta?id=${this.feature.id}`)
            const detaildata = await detailrequest.json()

            this.bupImage = detaildata.background_image
            this.released = detaildata.released
            this.genres = detaildata.genres || []
            this.metacritic = detaildata.metacritic
            this.rating = detaildata.rating
            this.ratingsCount = detaildata.ratings_count
            this.esrbRating = detaildata.esrb_rating
            this.developers = detaildata.developers || []
            this.descriptionRaw = detaildata.description_raw
        },
        async getTrailer(){
            const req = await window.afetch(`/game/trailer?id=${this.feature.id}`)
            const res = await req.json()
            const links = res.links

            if(links.length > 0){
                this.trailerLink = links[0]
            }

        }
    },
}
</script>

<style lang="scss" scoped>
.featured-game {
    position: relative;
    margin-bottom: 40px;
    height: 62vh;
    min-height: 780px;
    overflow: hidden;

    .featured-cover {
        position: absolute;
        inset: 0;
        video{
            position: aboslute;
            min-width: 100%;
            min-height: 100%;
            height: auto;
            object-fit: cover;
            opacity: 0.8;
            top: 0;
            left: 0;
        }

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        .featured-gradient {
            position: absolute;
            inset: 0;
            background: linear-gradient(to right, rgba(20, 20, 20, 0.94) 0%, rgba(20, 20, 20, 0.55) 55%, rgba(20, 20, 20, 0.25) 100%);
        }
    }

    .featured-content {
        position: relative;
        z-index: 1;
        height: 100%;
        display: flex;
        align-items: flex-end;
        gap: 44px;
        padding: 0 56px 56px;
        margin-top: -48px;

        .featured-poster {
            flex-shrink: 0;

            img {
                width: 260px;
                height: auto;
                display: block;
                border-radius: 10px;
                box-shadow: -4px 7px 20px 6px rgb(0 0 0 / 52%), 0 0 0 1px rgba(255, 255, 255, 0.08);
            }
        }

        .featured-info {
            display: flex;
            flex-direction: column;
            gap: 16px;
            max-width: 620px;
        }

        .featured-title {
            margin: 0;
            font-size: 42px;
            font-weight: 700;
            line-height: 1.15;
            letter-spacing: -0.01em;
            text-shadow: 0 2px 16px rgba(0, 0, 0, 0.45);
        }

        .featured-meta {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 10px;
            font-size: 14px;
            color: rgba(255, 255, 255, 0.7);

            .meta-genres {
                display: flex;
                gap: 8px;
            }

            .genre-pill {
                padding: 3px 11px;
                border-radius: 20px;
                border: 1px solid rgba(255, 255, 255, 0.28);
                background: rgba(255, 255, 255, 0.06);
                font-size: 12px;
                color: rgba(255, 255, 255, 0.85);
            }
        }

        .featured-badges {
            display: flex;
            align-items: center;
            gap: 16px;

            .badge-metascore {
                width: 36px;
                height: 36px;
                border-radius: 6px;
                display: flex;
                align-items: center;
                justify-content: center;
                font-weight: 700;
                font-size: 15px;
                color: #0f0f0f;

                &.high { background-color: #66cc33; }
                &.mid { background-color: #ffcc33; }
                &.low {
                    background-color: #ff3333;
                    color: #fff;
                }
            }

            .badge-rating {
                display: flex;
                align-items: center;
                gap: 8px;

                .star-rating {
                    position: relative;
                    display: inline-block;
                    font-size: 16px;
                    letter-spacing: 2px;

                    .stars-back {
                        color: rgba(255, 255, 255, 0.25);
                    }

                    .stars-front {
                        position: absolute;
                        top: 0;
                        left: 0;
                        overflow: hidden;
                        white-space: nowrap;
                        color: #f5c542;
                    }
                }

                .rating-value {
                    font-size: 14px;
                    font-weight: 600;
                    color: rgba(255, 255, 255, 0.9);
                }

                .rating-count {
                    font-size: 13px;
                    color: rgba(255, 255, 255, 0.5);
                }
            }

            .badge-esrb {
                padding: 3px 8px;
                border: 1px solid rgba(255, 255, 255, 0.35);
                border-radius: 4px;
                font-size: 12px;
                font-weight: 700;
                color: rgba(255, 255, 255, 0.85);
            }
        }

        .featured-description {
            margin: 0;
            max-width: 58ch;
            font-size: 15px;
            line-height: 1.6;
            color: rgba(255, 255, 255, 0.75);
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
        }

        .play-button {
            align-self: flex-start;
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 20px;
            background-color: rgba(#0984e3, 0.45);
            border: 1px solid #0984e3;
            border-radius: 6px;
            color: white;
            font-size: 12px;
            text-transform: uppercase;
            letter-spacing: 1px;
            cursor: pointer;
            width: fit-content;
            transition: background-color 0.2s;

            &:hover {
                background-color: rgba(#0984e3, 0.7);
            }

            svg {
                width: 16px;
                height: 16px;
            }
        }
    }
}
</style>
